import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { MapPin, Navigation, Star, Clock, Car, CheckCircle, Loader2, ArrowLeft, ArrowRight, AlertCircle, Lock, Calendar } from 'lucide-react';
import AddressAutocomplete, { geocodeAddress } from '@/components/AddressAutocomplete';

// Gera uma chave de idempotência única
const generateIdempotencyKey = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Calcula o início (segunda) e fim (domingo) de uma semana
const getWeekBoundaries = (dateStr) => {
  const date = new Date(dateStr + 'T12:00:00');
  const dayOfWeek = date.getDay(); // 0=domingo, 1=segunda, ..., 6=sábado
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  
  const weekStart = new Date(date);
  weekStart.setDate(date.getDate() - daysToMonday);
  
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  
  return {
    start: weekStart.toISOString().split('T')[0],
    end: weekEnd.toISOString().split('T')[0]
  };
};

export default function ClientSchedule() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  // Pass 9C: hand-off vindo de /client/providers (React Router state). Apenas PRÉ-SELEÇÃO
  // visual do prestador + localização já informada na descoberta. NÃO cria reserva, NÃO
  // consome cota, NÃO reserva slot. Sem state (acesso direto/reload) => fluxo normal intacto.
  const routerLocation = useLocation();
  const handoffProvider = routerLocation.state?.preSelectedProvider || null;
  const handoffLocation = routerLocation.state?.location || null;
  const [preSelectedProvider] = useState(handoffProvider);
  const handoffAutoSelectedRef = useRef(false);
  
  // Estados do fluxo
  const [step, setStep] = useState(1); // 1: Data/Hora, 2: Localização, 3: Selecionar Prestador, 4: Confirmação
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Estados de validação do plano
  const [schedulingStatus, setSchedulingStatus] = useState(null);
  const [availableServices, setAvailableServices] = useState([]);
  const [blockedWeek, setBlockedWeek] = useState(null); // { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' }
  const [monthlyBlocked, setMonthlyBlocked] = useState(false);
  const [blockMessage, setBlockMessage] = useState(null);
  
  // Dados do formulário
  const [form, setForm] = useState({
    date: '',
    time: '',
    service_type: ''
  });
  
  // Dados de localização (Pass 9C: pré-preenche com a localização da descoberta, se houver)
  const [location, setLocation] = useState(handoffLocation ? {
    latitude: handoffLocation.latitude ?? null,
    longitude: handoffLocation.longitude ?? null,
    address: handoffLocation.address ?? '',
    method: handoffLocation.method ?? null
  } : {
    latitude: null,
    longitude: null,
    address: '',
    method: null // 'gps' ou 'manual'
  });
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  
  // Veículos e prestadores
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [searchingProviders, setSearchingProviders] = useState(false);

  // ==================== FIX: Sempre buscar dados atualizados ====================
  // Efeito inicial - verificar onboarding e carregar dados
  useEffect(() => {
    if (!user || user.user_type !== 'client') {
      navigate('/client/login');
      return;
    }
    
    // Verificar onboarding e status de agendamento
    checkOnboardingAndFetchData();
  }, [user, navigate]);

  // FIX: Recarregar serviços disponíveis quando o plano do usuário mudar
  // Isso garante que após uma troca de plano, os dados sejam atualizados
  useEffect(() => {
    if (user?.subscription_id || user?.plan_id) {
      // Forçar reload dos serviços disponíveis quando subscription ou plan mudar
      fetchSchedulingStatus();
    }
  }, [user?.subscription_id, user?.plan_id]);

  // FIX: Recarregar dados quando a página ganhar foco (usuário voltou de outra aba)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user?.id) {
        // Recarregar status de agendamento quando a aba ficar visível
        fetchSchedulingStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user?.id]);

  // Verificar status de agendamento quando a data muda
  useEffect(() => {
    if (form.date) {
      checkDateAvailability(form.date);
    }
  }, [form.date]);

  const checkOnboardingAndFetchData = async () => {
    setInitialLoading(true);
    try {
      const token = localStorage.getItem('token');
      const onboardingRes = await axios.get(`${API}/client/onboarding-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Se onboarding não está completo, redirecionar
      if (!onboardingRes.data.onboarding_complete) {
        toast.error('Complete seu cadastro antes de agendar');
        if (onboardingRes.data.next_step === 'vehicle') {
          navigate('/client/onboarding/vehicle', { replace: true });
        } else if (onboardingRes.data.next_step === 'plan') {
          navigate('/client/onboarding/plan', { replace: true });
        }
        return;
      }
      
      // Se não tem plano ativo, bloquear agendamento
      if (!onboardingRes.data.has_active_plan) {
        toast.error('Você precisa de um plano ativo para agendar');
        navigate('/client/plans', { replace: true });
        return;
      }
      
      // Buscar status de agendamento e serviços disponíveis
      await Promise.all([
        fetchVehicles(),
        fetchSchedulingStatus()
      ]);
    } catch (error) {
      console.error('Erro ao verificar onboarding:', error);
      await fetchVehicles();
    } finally {
      setInitialLoading(false);
    }
  };

  // Buscar status de agendamento e serviços disponíveis
  // FIX: Esta função agora é chamada sempre que a tela abre ou o plano muda
  const fetchSchedulingStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // FIX: Limpar estados antigos antes de buscar novos dados
      // Isso evita que dados do plano anterior sejam exibidos durante o carregamento
      setAvailableServices([]);
      setMonthlyBlocked(false);
      setBlockMessage(null);
      
      // Buscar serviços disponíveis DIRETAMENTE do backend
      // NÃO usar dados do contexto user que podem estar desatualizados
      const availableRes = await axios.get(`${API}/appointments/available-services`, { headers });
      setSchedulingStatus(availableRes.data);
      
      console.log('[Schedule] Serviços disponíveis atualizados:', availableRes.data);
      
      if (!availableRes.data.has_subscription) {
        setMonthlyBlocked(true);
        setBlockMessage('Você não possui uma assinatura ativa. Assine um plano para agendar serviços.');
        return;
      }
      
      if (!availableRes.data.can_schedule) {
        setMonthlyBlocked(true);
        setBlockMessage(availableRes.data.message || 'Seu plano atingiu o limite mensal de serviços. Aguarde a renovação da assinatura.');
        return;
      }
      
      // Definir serviços disponíveis
      const services = availableRes.data.available_services || [];
      setAvailableServices(services);
      
      // FIX: Resetar seleção de serviço se o tipo atual não está mais disponível
      const currentServiceType = form.service_type;
      const serviceStillAvailable = services.some(s => s.type === currentServiceType && s.remaining > 0);
      
      if (!serviceStillAvailable && services.length > 0) {
        // Selecionar o primeiro serviço disponível automaticamente
        const firstAvailable = services.find(s => s.remaining > 0);
        if (firstAvailable) {
          setForm(prev => ({ ...prev, service_type: firstAvailable.type }));
        }
      } else if (!currentServiceType && services.length > 0) {
        // Se não tinha nenhum selecionado, selecionar o primeiro
        setForm(prev => ({ ...prev, service_type: services[0].type }));
      }
      
      // Verificar limite semanal para hoje
      await checkWeeklyLimit();
      
    } catch (error) {
      console.error('Erro ao buscar status de agendamento:', error);
    }
  };

  // Verificar limite semanal
  const checkWeeklyLimit = async (targetDate = null) => {
    try {
      const token = localStorage.getItem('token');
      const dateParam = targetDate || new Date().toISOString().split('T')[0];
      
      const res = await axios.get(`${API}/appointments/weekly-status?date=${dateParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.data.can_schedule && res.data.existing_appointment) {
        // Bloquear a semana atual
        setBlockedWeek({
          start: res.data.week_start,
          end: res.data.week_end
        });
      } else {
        setBlockedWeek(null);
      }
      
      return res.data;
    } catch (error) {
      console.error('Erro ao verificar limite semanal:', error);
      return { can_schedule: true };
    }
  };

  // Verificar se uma data específica está disponível
  const checkDateAvailability = async (dateStr) => {
    if (!dateStr) return;
    
    const weeklyStatus = await checkWeeklyLimit(dateStr);
    
    if (!weeklyStatus.can_schedule) {
      // A data está em uma semana bloqueada
      setBlockedWeek({
        start: weeklyStatus.week_start,
        end: weeklyStatus.week_end
      });
    }
  };

  // Verificar se uma data está bloqueada pela regra semanal
  const isDateBlocked = (dateStr) => {
    if (!blockedWeek) return false;
    return dateStr >= blockedWeek.start && dateStr <= blockedWeek.end;
  };

  // Obter a primeira data disponível (após a semana bloqueada)
  const getFirstAvailableDate = () => {
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    
    if (!blockedWeek) {
      return today.toISOString().split('T')[0];
    }
    
    // Próximo dia após o fim da semana bloqueada
    const nextAvailable = new Date(blockedWeek.end + 'T12:00:00');
    nextAvailable.setDate(nextAvailable.getDate() + 1);
    
    // Se a próxima data disponível é anterior a hoje, usar hoje
    if (nextAvailable < today) {
      return today.toISOString().split('T')[0];
    }
    
    return nextAvailable.toISOString().split('T')[0];
  };

  const fetchVehicles = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // FIX: Buscar o veículo da assinatura ativa PRIMEIRO
      // Este é o veículo correto para agendamentos
      const availableRes = await axios.get(`${API}/appointments/available-services`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Se tem veículo da assinatura ativa, usar este
      if (availableRes.data.vehicle) {
        const activeVehicle = availableRes.data.vehicle;
        console.log('[Schedule] Usando veículo da assinatura ativa:', activeVehicle.brand, activeVehicle.model);
        setVehicles([activeVehicle]);
        setSelectedVehicle(activeVehicle.id);
        return;
      }
      
      // Fallback: Se não encontrou veículo da assinatura, tentar lista completa
      const res = await axios.get(`${API}/client/vehicles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.vehicles && res.data.vehicles.length > 0) {
        setVehicles(res.data.vehicles);
        // NÃO selecionar automaticamente - deixar o usuário escolher
        // ou buscar da subscription
        const subRes = await axios.get(`${API}/client/subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (subRes.data.subscription?.vehicle_id) {
          const activeVehicleId = subRes.data.subscription.vehicle_id;
          const activeVehicle = res.data.vehicles.find(v => v.id === activeVehicleId);
          if (activeVehicle) {
            setSelectedVehicle(activeVehicle.id);
            return;
          }
        }
        
        // Último fallback: primeiro veículo da lista
        setSelectedVehicle(res.data.vehicles[0].id);
        return;
      }
      
      // Se não encontrou veículos na lista, tentar buscar da subscription ativa
      const subRes = await axios.get(`${API}/client/subscription-details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (subRes.data.has_active_plan && subRes.data.subscription?.vehicle) {
        const activeVehicle = subRes.data.subscription.vehicle;
        setVehicles([activeVehicle]);
        setSelectedVehicle(activeVehicle.id);
      }
    } catch (error) {
      console.error('Erro ao buscar veículos:', error);
    }
  };

  // ETAPA 1: Validar e avançar para localização
  const handleContinueToLocation = async () => {
    if (!form.date || !form.time) {
      toast.error('Por favor, selecione data e horário');
      return;
    }
    
    if (!selectedVehicle) {
      toast.error('Por favor, selecione um veículo');
      return;
    }
    
    if (!form.service_type) {
      toast.error('Por favor, selecione o tipo de serviço');
      return;
    }
    
    // Validar data/hora não passada — SEMPRE no fuso de negócio America/Sao_Paulo,
    // independentemente do timezone do dispositivo/WebView (no APK Release o WebView roda em UTC).
    // Comparação por STRING "YYYY-MM-DDTHH:mm" evita qualquer parsing de Date entre fusos.
    const _spParts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(new Date());
    const _p = Object.fromEntries(_spParts.map((x) => [x.type, x.value]));
    const nowSaoPaulo = `${_p.year}-${_p.month}-${_p.day}T${_p.hour === '24' ? '00' : _p.hour}:${_p.minute}`;
    const selectedSaoPaulo = `${form.date}T${form.time}`;
    if (selectedSaoPaulo < nowSaoPaulo) {
      toast.error('Não é possível agendar em datas/horários passados');
      return;
    }
    
    // Verificar se a data está bloqueada pela regra semanal
    if (isDateBlocked(form.date)) {
      toast.error('Você já possui um agendamento nesta semana. Escolha uma data a partir da próxima semana.');
      return;
    }
    
    // Validar novamente com o backend antes de prosseguir
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Validação completa no backend
      const statusRes = await axios.get(
        `${API}/appointments/scheduling-status?date=${form.date}&service_type=${form.service_type}`,
        { headers }
      );
      
      if (!statusRes.data.can_schedule) {
        toast.error(statusRes.data.message || 'Não foi possível agendar nesta data');
        
        // Atualizar estado se necessário
        if (!statusRes.data.weekly_status?.allowed) {
          setBlockedWeek({
            start: statusRes.data.weekly_status.week_start,
            end: statusRes.data.weekly_status.week_end
          });
        }
        if (!statusRes.data.monthly_status?.allowed) {
          setMonthlyBlocked(true);
          setBlockMessage(statusRes.data.monthly_status.message);
        }
        return;
      }
      
      // Pass 10C-V: se a localização já foi obtida no hand-off de /client/providers
      // (prestador pré-selecionado + coordenadas válidas), NÃO reabrir o modal "Sua Localização".
      // Prosseguir direto para a busca de prestadores (que fará o auto-select e levará à confirmação).
      // Fluxo de entrada direta no Schedule (sem hand-off) mantém o modal como antes.
      if (preSelectedProvider && location.latitude && location.longitude) {
        setStep(2);
        searchProviders(location.latitude, location.longitude);
      } else {
        setLocationModalOpen(true);
      }
    } catch (error) {
      console.error('Erro ao validar agendamento:', error);
      toast.error('Erro ao validar agendamento. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // ETAPA 2: Obter localização por GPS
  // Em Capacitor (Android/iOS) usa o plugin @capacitor/geolocation, que dispara
  // corretamente o diálogo nativo de permissão do sistema (não faz sentido usar
  // navigator.geolocation direto: em contexto Capacitor com origem local, o SO
  // Android nega imediatamente sem exibir o prompt).
  // Fluxo: checkPermissions() → se prompt, requestPermissions() → getCurrentPosition().
  const getGPSLocation = async () => {
    setGettingLocation(true);

    const handleSuccess = (latitude, longitude) => {
      setLocation({
        latitude,
        longitude,
        address: 'Localização atual via GPS',
        method: 'gps'
      });
      setGettingLocation(false);
      setLocationModalOpen(false);
      setStep(2);
      searchProviders(latitude, longitude);
    };

    // Capacitor nativo (Android/iOS) — usa o plugin oficial
    if (Capacitor.isNativePlatform()) {
      try {
        let perm = await Geolocation.checkPermissions();
        if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') {
          perm = await Geolocation.requestPermissions({ permissions: ['location'] });
        }
        if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') {
          setGettingLocation(false);
          toast.error('Permissão de localização negada. Ative nas configurações do app se necessário.');
          return;
        }
        const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 });
        handleSuccess(pos.coords.latitude, pos.coords.longitude);
      } catch (err) {
        setGettingLocation(false);
        const msg = String(err?.message || err || '').toLowerCase();
        if (msg.includes('denied') || msg.includes('permission')) {
          toast.error('Permissão de localização negada. Ative nas configurações do app se necessário.');
        } else if (msg.includes('unavailable') || msg.includes('disabled')) {
          toast.error('GPS indisponível ou desligado. Ative a localização do dispositivo.');
        } else if (msg.includes('timeout')) {
          toast.error('Tempo esgotado ao obter localização.');
        } else {
          toast.error('Erro ao obter localização.');
        }
      }
      return;
    }

    // Web / PWA — usa a API do navegador
    if (!navigator.geolocation) {
      setGettingLocation(false);
      toast.error('Geolocalização não suportada pelo navegador');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => handleSuccess(position.coords.latitude, position.coords.longitude),
      (error) => {
        setGettingLocation(false);
        let errorMsg = 'Erro ao obter localização';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMsg = 'Permissão de localização negada';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg = 'Localização indisponível';
            break;
          case error.TIMEOUT:
            errorMsg = 'Tempo esgotado ao obter localização';
            break;
        }
        toast.error(errorMsg);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // ETAPA 2: Usar endereço manual (simplificado - em produção usar API de geocoding)
  const useManualAddress = async () => {
    if (!location.address.trim()) {
      toast.error('Por favor, informe o endereço');
      return;
    }
    
    // Se já tem coordenadas (selecionou da lista de sugestões), usar diretamente
    if (location.latitude && location.longitude) {
      setLocationModalOpen(false);
      setStep(2);
      searchProviders(location.latitude, location.longitude);
      return;
    }
    
    // Fallback: fazer geocoding do endereço digitado
    setGettingLocation(true);
    try {
      const result = await geocodeAddress(location.address);
      
      if (!result) {
        toast.error('Não foi possível encontrar este endereço. Tente ser mais específico.');
        return;
      }
      
      setLocation(prev => ({
        ...prev,
        latitude: result.latitude,
        longitude: result.longitude,
        method: 'manual'
      }));
      
      setLocationModalOpen(false);
      setStep(2);
      searchProviders(result.latitude, result.longitude);
    } catch (error) {
      toast.error('Erro ao buscar endereço. Tente novamente.');
    } finally {
      setGettingLocation(false);
    }
  };

  // Handler para seleção de endereço do autocomplete
  const handleAddressSelect = (selectedAddress) => {
    setLocation(prev => ({
      ...prev,
      address: selectedAddress.address,
      latitude: selectedAddress.latitude,
      longitude: selectedAddress.longitude,
      method: 'manual'
    }));
  };

  // ETAPA 3: Buscar prestadores próximos
  const searchProviders = async (lat, lon) => {
    setSearchingProviders(true);
    setProviders([]);
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/appointments/find-providers`, {
        latitude: lat,
        longitude: lon,
        service_type: form.service_type,
        date: form.date,
        time: form.time
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProviders(res.data.providers);
      setStep(3);
      
      if (res.data.providers.length === 0) {
        toast.warning('Nenhum prestador disponível neste horário');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao buscar prestadores');
    } finally {
      setSearchingProviders(false);
    }
  };

  // ETAPA 4: Selecionar prestador
  const selectProvider = (provider) => {
    setSelectedProvider(provider);
    setStep(4);
  };

  // Pass 9C: quando a lista do Step 3 carregar, se houver prestador pré-selecionado na
  // descoberta e ele estiver disponível para a data/hora escolhidas, seleciona-o
  // automaticamente (reutiliza selectProvider — NÃO cria reserva). Se não estiver na lista
  // (indisponível no horário), mantém o Step 3 normal para nova escolha.
  useEffect(() => {
    if (!preSelectedProvider) return;
    if (step === 3 && providers.length > 0 && !handoffAutoSelectedRef.current) {
      const pid = preSelectedProvider.id || preSelectedProvider.provider_id;
      const match = providers.find(p => p.id === pid);
      if (match) {
        handoffAutoSelectedRef.current = true;
        selectProvider(match);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, providers, preSelectedProvider]);

  // ETAPA 5: Confirmar agendamento
  const confirmAppointment = async () => {
    if (!selectedProvider || !selectedVehicle) {
      toast.error('Dados incompletos');
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const idempotencyKey = generateIdempotencyKey();
      
      await axios.post(`${API}/appointments/schedule`, {
        provider_id: selectedProvider.id,
        vehicle_id: selectedVehicle,
        date: form.date,
        time: form.time,
        service_type: form.service_type,
        latitude: location.latitude,
        longitude: location.longitude,
        idempotency_key: idempotencyKey
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Agendamento confirmado! Aguardando confirmação do prestador.');
      navigate('/client/appointments');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao confirmar agendamento';
      toast.error(errorMsg);
      
      // Se horário ocupado, voltar para busca de prestadores
      if (error.response?.status === 409) {
        setStep(2);
        searchProviders(location.latitude, location.longitude);
      }
    } finally {
      setLoading(false);
    }
  };

  // Voltar etapa
  const goBack = () => {
    if (step === 2 || step === 3) {
      setStep(1);
      setProviders([]);
      setSelectedProvider(null);
    } else if (step === 4) {
      setStep(3);
      setSelectedProvider(null);
    }
  };

  // Renderizar etapa atual
  const renderStep = () => {
    switch (step) {
      case 1:
        return renderDateTimeStep();
      case 2:
        return renderSearchingStep();
      case 3:
        return renderProvidersStep();
      case 4:
        return renderConfirmationStep();
      default:
        return renderDateTimeStep();
    }
  };

  // ETAPA 1: Seleção de data, hora e serviço
  const renderDateTimeStep = () => {
    // Se carregando inicial
    if (initialLoading) {
      return (
        <Card className="w-full max-w-md p-8 text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary mb-4" />
          <p className="text-muted-foreground">Verificando disponibilidade...</p>
        </Card>
      );
    }
    
    // Se o limite mensal foi atingido, mostrar bloqueio total
    if (monthlyBlocked) {
      return (
        <Card className="w-full max-w-md p-8">
          <div className="flex items-center gap-3 mb-6">
            <Lock className="w-8 h-8 text-red-500" />
            <h1 className="text-2xl font-bold text-red-600">Limite Atingido</h1>
          </div>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">
              {blockMessage || 'Você já utilizou todos os serviços do seu plano neste ciclo mensal. Novos agendamentos estarão disponíveis após a renovação da assinatura.'}
            </p>
          </div>
          
          {schedulingStatus && (
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <h3 className="font-medium mb-2">Resumo do seu plano:</h3>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>Lavagens Completas: {schedulingStatus.services_used?.complete || 0}/{schedulingStatus.limits?.complete || 0}</p>
                <p>Duchas: {schedulingStatus.services_used?.ducha || 0}/{schedulingStatus.limits?.ducha || 0}</p>
              </div>
            </div>
          )}
          
          <Button 
            variant="outline" 
            onClick={() => navigate('/client/dashboard')} 
            className="w-full"
          >
            Voltar ao Dashboard
          </Button>
        </Card>
      );
    }
    
    // Calcular min date considerando bloqueio semanal
    const today = new Date().toISOString().split('T')[0];
    const minDate = blockedWeek ? getFirstAvailableDate() : today;
    
    return (
      <Card className="w-full max-w-md p-8">
        <div className="flex items-center gap-3 mb-6">
          <Clock className="w-8 h-8 text-primary" />
          <h1 className="text-2xl font-bold" data-testid="schedule-title">Agendar Lavagem</h1>
        </div>

        {/* Pass 9C: identificação visual do lava-jato pré-selecionado vindo de /client/providers */}
        {preSelectedProvider && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center gap-2" data-testid="preselected-provider-banner">
            <MapPin className="w-4 h-4 text-blue-600 shrink-0" />
            <p className="text-sm text-blue-800">
              Lava-jato selecionado: <strong>{preSelectedProvider.business_name || preSelectedProvider.name}</strong>
            </p>
          </div>
        )}
        
        {/* Alerta de bloqueio semanal */}
        {blockedWeek && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-2">
              <Calendar className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="text-amber-800 font-medium">Você já possui um agendamento nesta semana</p>
                <p className="text-amber-700 text-sm mt-1">
                  Novos agendamentos disponíveis a partir de <strong>{formatDateBR(getFirstAvailableDate())}</strong>
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Resumo de serviços disponíveis */}
        {availableServices.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <p className="text-green-800 text-sm font-medium mb-2">Serviços disponíveis no seu plano:</p>
            <div className="flex flex-wrap gap-2">
              {availableServices.map(service => (
                <Badge 
                  key={service.type} 
                  variant="outline" 
                  className="bg-white border-green-300 text-green-700"
                >
                  {service.name}: {Math.max(0, (service.total || 0) - (service.remaining || 0))}/{service.total}
                </Badge>
              ))}
            </div>
          </div>
        )}
        
        <div className="space-y-4">
          {/* Veículo */}
          <div>
            <Label htmlFor="vehicle">Veículo</Label>
            {vehicles.length > 0 ? (
              <Select value={selectedVehicle || ''} onValueChange={setSelectedVehicle}>
                <SelectTrigger data-testid="vehicle-select">
                  <SelectValue placeholder="Selecione o veículo" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.brand} {v.model} - {v.plate}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="p-3 bg-yellow-50 text-yellow-800 rounded-md text-sm">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                Você precisa cadastrar um veículo primeiro.
                <Button variant="link" className="p-0 ml-2 h-auto" onClick={() => navigate('/client/vehicle')}>
                  Cadastrar
                </Button>
              </div>
            )}
          </div>
          
          {/* Data */}
          <div>
            <Label htmlFor="date">Data</Label>
            <Input
              id="date"
              type="date"
              data-testid="schedule-date-input"
              value={form.date}
              min={minDate}
              onChange={(e) => {
                const newDate = e.target.value;
                // Verificar se a data está bloqueada
                if (isDateBlocked(newDate)) {
                  toast.error('Esta data está bloqueada. Você já possui um agendamento nesta semana.');
                  return;
                }
                setForm({ ...form, date: newDate });
              }}
              required
            />
            {blockedWeek && form.date && isDateBlocked(form.date) && (
              <p className="text-red-500 text-xs mt-1">
                Esta data está bloqueada pela regra semanal
              </p>
            )}
          </div>
          
          {/* Hora */}
          <div>
            <Label htmlFor="time">Horário</Label>
            <Select value={form.time} onValueChange={(value) => setForm({ ...form, time: value })}>
              <SelectTrigger data-testid="time-select">
                <SelectValue placeholder="Selecione o horário" />
              </SelectTrigger>
              <SelectContent>
                {/* SAFE PATCH: Horários de 08:00 até 18:00 (21 slots) */}
                {Array.from({ length: 21 }, (_, i) => {
                  const hour = Math.floor(i / 2) + 8;
                  const min = i % 2 === 0 ? '00' : '30';
                  const time = `${hour.toString().padStart(2, '0')}:${min}`;
                  return (
                    <SelectItem key={time} value={time}>
                      {time}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          
          {/* Tipo de serviço - Filtrado baseado nos serviços disponíveis */}
          <div>
            <Label htmlFor="service_type">Tipo de Serviço</Label>
            {availableServices.length > 0 ? (
              <Select value={form.service_type} onValueChange={(value) => setForm({ ...form, service_type: value })}>
                <SelectTrigger data-testid="service-type-select">
                  <SelectValue placeholder="Selecione o serviço" />
                </SelectTrigger>
                <SelectContent>
                  {availableServices.map(service => (
                    <SelectItem key={service.type} value={service.type}>
                      {service.name} ({service.remaining} restante{service.remaining !== 1 ? 's' : ''})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Select value={form.service_type} onValueChange={(value) => setForm({ ...form, service_type: value })}>
                <SelectTrigger data-testid="service-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="complete">Lavagem Completa</SelectItem>
                  <SelectItem value="ducha">Ducha Simples</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
          
          <Button 
            onClick={handleContinueToLocation} 
            className="w-full" 
            disabled={!selectedVehicle || !form.date || !form.time || !form.service_type || loading || isDateBlocked(form.date)}
            data-testid="continue-to-location-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Validando...
              </>
            ) : (
              <>
                Continuar <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
          
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => navigate('/client/dashboard')} 
            className="w-full"
          >
            Voltar
          </Button>
        </div>
      </Card>
    );
  };

  // Função para formatar data no padrão brasileiro
  const formatDateBR = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  // ETAPA 2: Buscando prestadores
  const renderSearchingStep = () => (
    <Card className="w-full max-w-md p-8 text-center">
      <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary mb-4" />
      <h2 className="text-xl font-semibold mb-2">Buscando lava-jatos próximos...</h2>
      <p className="text-muted-foreground">
        Localizando prestadores disponíveis para {formatDateBR(form.date)} às {form.time}
      </p>
    </Card>
  );

  // ETAPA 3: Lista de prestadores
  const renderProvidersStep = () => (
    <Card className="w-full max-w-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <MapPin className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Lava-jatos Próximos</h1>
            <p className="text-sm text-muted-foreground">
              {formatDateBR(form.date)} às {form.time} - {form.service_type === 'complete' ? 'Lavagem Completa' : 'Ducha Simples'}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={goBack}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Alterar horário
        </Button>
      </div>
      
      {providers.length === 0 ? (
        <div className="text-center py-8">
          <AlertCircle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Nenhum prestador disponível</h3>
          <p className="text-muted-foreground mb-4">
            Não encontramos lava-jatos disponíveis para este horário.
          </p>
          <Button onClick={goBack}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Escolher outro horário
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {providers.map(provider => (
            <Card 
              key={provider.id} 
              className="p-4 hover:bg-slate-50 cursor-pointer transition-colors border-2 hover:border-primary"
              onClick={() => selectProvider(provider)}
              data-testid={`provider-card-${provider.id}`}
            >
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">
                    {provider.business_name || provider.name}
                  </h3>
                  
                  {/* Endereço curto */}
                  {provider.address && (provider.address.street || provider.address.number) && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {provider.address.street}{provider.address.number ? `, ${provider.address.number}` : ''}
                    </p>
                  )}
                  
                  <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                      {provider.rating?.average && provider.rating.average > 0 
                        ? (
                          <>
                            {provider.rating.average.toFixed(1)}
                            {provider.rating.count > 0 && (
                              <span className="text-xs">({provider.rating.count})</span>
                            )}
                          </>
                        )
                        : 'Novo'}
                    </span>
                    
                    <span>•</span>
                    
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {provider.distance_km} km
                    </span>
                  </div>
                </div>
                
                <Button size="sm" data-testid={`select-provider-${provider.id}`}>
                  Selecionar
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </Card>
  );

  // ETAPA 4: Confirmação
  const renderConfirmationStep = () => {
    const vehicle = vehicles.find(v => v.id === selectedVehicle);
    
    return (
      <Card className="w-full max-w-md p-6">
        <div className="flex items-center gap-3 mb-6">
          <CheckCircle className="w-8 h-8 text-green-600" />
          <h1 className="text-2xl font-bold">Confirmar Agendamento</h1>
        </div>
        
        <div className="space-y-4 mb-6">
          <div className="bg-slate-50 p-4 rounded-lg space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Lava-jato:</span>
              <span className="font-semibold">{selectedProvider?.business_name || selectedProvider?.name}</span>
            </div>
            {/* Endereço completo */}
            {selectedProvider?.address && (selectedProvider.address.street || selectedProvider.address.city) && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Endereço:</span>
                <span className="text-right text-sm">
                  {selectedProvider.address.street}{selectedProvider.address.number ? `, ${selectedProvider.address.number}` : ''}
                  {selectedProvider.address.city && (
                    <> – {selectedProvider.address.city}{selectedProvider.address.state ? `/${selectedProvider.address.state}` : ''}</>
                  )}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Distância:</span>
              <span>{selectedProvider?.distance_km} km</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Data:</span>
              <span>{formatDateBR(form.date)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Horário:</span>
              <span>{form.time}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Serviço:</span>
              <span>{form.service_type === 'complete' ? 'Lavagem Completa' : 'Ducha Simples'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Veículo:</span>
              <span>{vehicle?.brand} {vehicle?.model}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Placa:</span>
              <span>{vehicle?.plate}</span>
            </div>
          </div>
          
          <div className="bg-yellow-50 p-3 rounded-lg text-sm text-yellow-800">
            <AlertCircle className="w-4 h-4 inline mr-2" />
            O prestador tem até 10 minutos para confirmar seu agendamento.
          </div>
        </div>
        
        <div className="space-y-3">
          <Button 
            onClick={confirmAppointment} 
            className="w-full" 
            disabled={loading}
            data-testid="confirm-appointment-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Confirmando...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Confirmar Agendamento
              </>
            )}
          </Button>
          
          <Button variant="outline" onClick={goBack} className="w-full" disabled={loading}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Escolher outro prestador
          </Button>
        </div>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      {renderStep()}
      
      {/* Modal de Localização */}
      <Dialog open={locationModalOpen} onOpenChange={setLocationModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Sua Localização
            </DialogTitle>
            <DialogDescription>
              Precisamos da sua localização para encontrar lava-jatos próximos.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Button 
              onClick={getGPSLocation} 
              className="w-full" 
              disabled={gettingLocation}
              data-testid="use-gps-btn"
            >
              {gettingLocation ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Obtendo localização...
                </>
              ) : (
                <>
                  <Navigation className="w-4 h-4 mr-2" />
                  Usar localização atual
                </>
              )}
            </Button>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-muted-foreground">ou</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="address">Informar endereço</Label>
              <AddressAutocomplete
                value={location.address}
                onChange={(address) => setLocation(prev => ({ 
                  ...prev, 
                  address,
                  // Limpar coordenadas se o usuário digitar novo texto
                  latitude: null,
                  longitude: null
                }))}
                onSelect={handleAddressSelect}
                placeholder="Digite o endereço (ex: Avenida Paulista, São Paulo)"
              />
              {location.latitude && location.longitude && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Endereço localizado
                </p>
              )}
              <Button 
                variant="outline" 
                onClick={useManualAddress} 
                className="w-full"
                disabled={!location.address.trim() || gettingLocation}
                data-testid="use-manual-address-btn"
              >
                {gettingLocation ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Buscando endereço...
                  </>
                ) : (
                  <>
                    <MapPin className="w-4 h-4 mr-2" />
                    Usar este endereço
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
